<script lang="ts">
	// The page a reset link opens (D18, UC-26): choose a new password.
	//
	// As on the sign-up link's page, the token is after the #, which no
	// browser sends to a server. The page reads it and takes it off the
	// address bar. Nothing checks the link until the password is sent: the
	// one request both spends the link and sets the password.

	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import Band from '../components/Band.svelte';
	import { message, type Language } from '../i18n/messages';
	import { passwordLengthOk, resetPassword } from '../owner/password';
	import { FORGOT_PATH, navigate } from '../router.svelte';

	interface Props {
		language: Language;
		/** The new password signed the owner in; the app reloads the session. */
		onSignedIn: () => void;
	}

	let { language, onSignedIn }: Props = $props();

	let token = '';
	$effect(() => {
		token = window.location.hash.slice(1);
		window.history.replaceState(window.history.state, '', window.location.pathname);
	});

	let password = $state('');
	let working = $state(false);
	let weak = $state(false);
	let failure = $state('');
	let dead = $state(false);

	async function save() {
		if (!password || working) return;
		failure = '';
		weak = !passwordLengthOk(password);
		if (weak) return;
		working = true;
		const result = await resetPassword(token, password);
		working = false;
		switch (result.kind) {
			case 'signedIn':
				onSignedIn();
				return;
			case 'weak':
				weak = true;
				return;
			case 'invalid':
				dead = true;
				return;
			default:
				failure = message(language, 'loadFailed');
		}
	}
</script>

<section class="screen" data-testid="screen-reset-link">
	<div class="card">
		<BrandMark size={26} />
		{#if dead}
			<div class="words" data-testid="reset-link-invalid">
				<h1>{message(language, 'linkInvalidTitle')}</h1>
				<p class="instruction">{message(language, 'resetInvalidText')}</p>
			</div>
			<div class="row">
				<Button
					testId="reset-link-forgot"
					label={message(language, 'forgotTitle')}
					tone="ink"
					size="md"
					onPress={() => navigate(FORGOT_PATH)}
				/>
			</div>
		{:else}
			<div class="words">
				<h1>{message(language, 'resetTitle')}</h1>
			</div>
			{#if failure}
				<Band tone="flag" testId="reset-failure">{failure}</Band>
			{/if}
			<Field
				testId="reset-password"
				label={message(language, 'resetPassword')}
				type="password"
				autocomplete="new-password"
				mono
				maxLength={128}
				value={password}
				note={message(language, 'passwordRule')}
				error={weak ? message(language, 'passwordRule') : undefined}
				errorTestId="reset-password-error"
				reveal={{
					show: message(language, 'passwordShow'),
					hide: message(language, 'passwordHide'),
					testId: 'reset-password-show',
				}}
				onInput={(value) => {
					password = value;
					weak = false;
				}}
			/>
			<Button
				testId="reset-submit"
				label={message(language, 'resetSubmit')}
				wide
				disabled={!password}
				state={working ? 'pending' : 'idle'}
				pendingLabel={message(language, 'saving')}
				onPress={() => void save()}
			/>
			<p class="note">{message(language, 'resetNote')}</p>
		{/if}
	</div>
</section>

<style>
	.screen {
		min-block-size: 100dvh;
		display: grid;
		place-items: center;
		padding: var(--space-9);
		background: var(--surface-page);
	}

	.card {
		inline-size: 100%;
		max-inline-size: 420px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
	}

	.words {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
		text-wrap: pretty;
	}

	.instruction {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.row {
		display: flex;
	}

	.note {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}
</style>
